import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as S from "./SaqueStyle";
import { formatNumber } from "../../assets/utils";
import Modal from "./SaqueModal/Modal";
import Loading from "../Loading/Loader";
import { AuthContext } from "../../context/AuthContext";
import SideBarBox from "../Sidebar/SideBarBox";
import TabelaDeSaques from "../Tabelas/TabelaSaques";
import { FiDownload, FiRepeat, FiInfo, FiX } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import useCountUp from "../../hooks/useCountUp";
import GoldenMiniLogo from "../GoldenMiniLogo/GoldenMiniLogo";
import WarningComunicadoSaque from "../Dashboard/Warning/WarningComunicadoSaque";

const COMUNICADO_SAQUE_SEEN_KEY = "comunicadoSaqueWarningSeen";

// Último instante de dezembro/2025 (fim do dia 31/12/2025).
const COMUNICADO_DATE_LIMIT = new Date("2025-12-31T23:59:59");

export default function Saque() {
  const { userData, platformData, contracts, withdraws } =
    useContext(AuthContext);
  const navigate = useNavigate();

  const [modalSaque, setModalSaque] = useState(false);
  const [showGoldenAd, setShowGoldenAd] = useState(false);
  const [showComunicado, setShowComunicado] = useState(false);
  const [loading, setLoading] = useState(true);

  const [contractsAvaliable, setContractsAvaliable] = useState([]);
  const [totalAvaliable, setTotalAvaliable] = useState(0);

  const animatedPlatformBalance = useCountUp(
    (platformData?.purchaseTotalBalance || 0) +
      (platformData?.platformWithdrawAvaliable || 0)
  );

  const handleValorDisponivelHoje = () => {
    let total = userData?.sponsorCredit || 0;

    const availableContracts = contracts
      ? contracts.filter(
          (c) =>
            (c.contract.status === 1 || c.contract.status === 2) &&
            c.avaliableWithdraw
        )
      : [];

    availableContracts.forEach((c) => {
      total += c.avaliableToWithdraw;
    });

    setTotalAvaliable(total);
  };

  useEffect(() => {
    if (platformData) {
      setLoading(false);
      handleValorDisponivelHoje();
    }
  }, [platformData, contracts, userData]);

  // Mesmo filtro de contratos do comunicado: contratos (status 1 ou 2) todos
  // comprados até o último dia de dezembro/2025. Se houver qualquer contrato
  // status 1 ou 2 comprado depois dessa data, não é elegível.
  const hasContractsUntilDec2025 = (contractsData) => {
    const relevantContracts = (contractsData || []).filter(
      (c) => c.contract?.status === 1 || c.contract?.status === 2
    );

    if (relevantContracts.length === 0) return false;

    const hasContractBeforeLimit = relevantContracts.some(
      (c) =>
        c.contract?.dateCreated &&
        new Date(c.contract.dateCreated) <= COMUNICADO_DATE_LIMIT
    );

    const hasContractAfterLimit = relevantContracts.some(
      (c) =>
        c.contract?.dateCreated &&
        new Date(c.contract.dateCreated) > COMUNICADO_DATE_LIMIT
    );

    return hasContractBeforeLimit && !hasContractAfterLimit;
  };

  // Comunicado na página de Saque: aparece se o cliente tem pelo menos um saque
  // com status 1 (Pendente) E também passa no filtro de contratos até dez/2025.
  useEffect(() => {
    if (!userData) return;
    if (sessionStorage.getItem(COMUNICADO_SAQUE_SEEN_KEY)) return;

    const hasPendingWithdraw = (withdraws || []).some(
      (w) => w.status === 1
    );

    setShowComunicado(
      hasPendingWithdraw && hasContractsUntilDec2025(contracts)
    );
  }, [userData, withdraws, contracts]);

  const handleCloseComunicado = () => {
    setShowComunicado(false);
    sessionStorage.setItem(COMUNICADO_SAQUE_SEEN_KEY, "true");
  };

  const proceedToSaque = () => {
    setShowGoldenAd(false);
    setModalSaque(true);

    const availableContracts = contracts
      ? contracts.filter(
          (c) =>
            (c.contract.status === 1 || c.contract.status === 2) &&
            c.avaliableWithdraw
        )
      : [];
    setContractsAvaliable(availableContracts);
  };

  const hasContractWithoutGoldenBox = () => {
    if (!contracts || contracts.length === 0) return false;
    return contracts.some((c) => {
      const isActive = c.contract.status === 1;
      const noGoldenBox = !c.contract.goldenBoxActived;
      return isActive && noGoldenBox;
    });
  };

  const handleClickRealizarSaque = () => {
    if (hasContractWithoutGoldenBox()) {
      setShowGoldenAd(true);
    } else {
      proceedToSaque();
    }
  };

  const handleWhatsappClick = () => {
    const phoneNumber = "5547988192616";
    const message =
      "Olá! Gostaria de saber mais sobre a proposta exclusiva para aumentar minha valorização antes de sacar.";
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
      message
    )}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <SideBarBox>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Loading />
        </div>
      </SideBarBox>
    );
  }

  return (
    <SideBarBox>
      {showComunicado && (
        <WarningComunicadoSaque onClose={handleCloseComunicado} />
      )}

      <S.SaquePageContainer>
        <S.Header>
          <S.Title>Saques e Valorização</S.Title>
        </S.Header>

        <S.WalletCard>
          <S.BalanceGroup className="main-balance">
            <S.BalanceLabel>Disponível para Saque (Hoje)</S.BalanceLabel>
            <S.BalanceValue>
              R$ {formatNumber(totalAvaliable || 0)}
            </S.BalanceValue>
          </S.BalanceGroup>

          <S.BalanceGroup>
            <S.BalanceLabel>Saldo de Indicação</S.BalanceLabel>
            <S.BalanceSubValue>
              R$ {formatNumber(userData?.sponsorCredit || 0)}
            </S.BalanceSubValue>
          </S.BalanceGroup>

          <S.BalanceGroup>
            <S.BalanceLabel>Valor Total em Plataforma</S.BalanceLabel>
            <S.BalanceSubValue>
              R$ {formatNumber(animatedPlatformBalance)}
            </S.BalanceSubValue>
          </S.BalanceGroup>

          <S.ActionsContainer>
            <S.ActionButton
              className="primary"
              onClick={handleClickRealizarSaque}
            >
              <FiDownload /> Realizar Saque
            </S.ActionButton>

            {userData?.reinvest && (
              <S.ActionButton className="secondary">
                <FiRepeat /> Reinvestir Saldo
              </S.ActionButton>
            )}
          </S.ActionsContainer>
        </S.WalletCard>

        <S.InfoCard>
          <FiInfo size={24} />
          <S.InfoText>
            <p>
              A solicitação de resgate da valorização é disponibilizada
              trimestralmente nos contratos que não estão configurados na Golden
              Box.
            </p>
            <p>
              Os pagamentos são processados entre o 1º e o 5º dia útil de cada
              mês.
            </p>
          </S.InfoText>
        </S.InfoCard>

        <S.TabelaContainer>
          <S.TabelaHeader>
            <h2>Histórico de Saques</h2>
          </S.TabelaHeader>
          <TabelaDeSaques />
        </S.TabelaContainer>

        {modalSaque && (
          <Modal
            contracts={contractsAvaliable.filter(
              (c) => !c.contract.goldenBoxActived
            )}
            handleModalSaque={() => setModalSaque(false)}
          />
        )}

        {showGoldenAd && (
          <S.AdOverlay>
            <S.AdContainer>
              <div className="shine-effect"></div>
              <S.AdCloseButton onClick={proceedToSaque}>
                <FiX />
              </S.AdCloseButton>

              <S.AdContent>
                <div className="icon-wrapper">
                  <GoldenMiniLogo />
                </div>

                <h2>Sabia que se você não sacar pode ter valorização extra?</h2>

                <div className="text-block">
                  <p className="highlight">
                    <strong>CHAME JÁ SEU CONSULTOR E FAÇA SEU SALDO VALORIZAR MAIS.</strong>
                  </p>
                </div>

                <S.AdButtons>
                  <button
                    className="btn-whatsapp"
                    onClick={handleWhatsappClick}
                  >
                    <FaWhatsapp size={20} /> CHAME JÁ SEU CONSULTOR
                  </button>

                  <button className="btn-link" onClick={proceedToSaque}>
                    Por enquanto não, quero apenas sacar
                  </button>
                </S.AdButtons>
              </S.AdContent>
            </S.AdContainer>
          </S.AdOverlay>
        )}
      </S.SaquePageContainer>
    </SideBarBox>
  );
}
